import {
  KeyboardEvent,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { TF } from 'react-translator-component';

import { connectorStatus } from './doorAccessMapping';
import DoorConnectorList from './DoorConnectorList';
import DoorConnectorPanel, {
  DoorCopySource,
  DoorPanelApi,
} from './DoorConnectorPanel';
import { DoorLayout } from './styled';

import { doorAccessApi, doorErrorMessage } from '../../../../hooks/doorAccess';
import { DoorSummary } from '../../../../types/responses/DoorAccess';

interface StationConnector {
  connectorId: number;
  connectorTypeId?: number | string | null;
}

export interface DoorSaveFailure {
  connectorId: number;
  error: string;
}

// O que o formulário da estação usa no "Enviar": valida e salva a porta de
// todos os conectores que foram editados nesta aba.
export interface DoorAccessFormApi {
  validate: () => Promise<boolean>;
  saveAll: () => Promise<DoorSaveFailure[]>;
}

interface StationDoorAccessFormProps {
  chargeBoxPk: string | number;
  connectors: StationConnector[];
  connectorTypesList: { list?: { id: number | string; name: string }[] };
  formApiRef?: MutableRefObject<DoorAccessFormApi | null>;
}

const StationDoorAccessForm = ({
  chargeBoxPk,
  connectors,
  connectorTypesList,
  formApiRef,
}: StationDoorAccessFormProps) => {
  const sorted = useMemo(
    () => [...(connectors || [])].sort((a, b) => a.connectorId - b.connectorId),
    [connectors],
  );
  const [summaries, setSummaries] = useState<DoorSummary[]>([]);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(
    sorted[0]?.connectorId ?? null,
  );
  const panels = useRef(new Map<number, DoorPanelApi>());
  const mounted = useRef(true);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  const loadSummary = useCallback(async () => {
    setSummaryError(null);
    try {
      const list = await doorAccessApi.getSummary(chargeBoxPk);
      if (mounted.current) setSummaries(list);
    } catch (error) {
      if (mounted.current) setSummaryError(doorErrorMessage(error));
    }
  }, [chargeBoxPk]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const handleSaved = useCallback((summary: DoorSummary) => {
    setSummaries((previous) => [
      ...previous.filter((s) => s.connectorId !== summary.connectorId),
      summary,
    ]);
  }, []);

  const register = useCallback((connectorId: number, api: DoorPanelApi) => {
    panels.current.set(connectorId, api);
    return () => {
      panels.current.delete(connectorId);
    };
  }, []);

  useEffect(() => {
    if (!formApiRef) return undefined;
    const dirtyPanels = () =>
      Array.from(panels.current.entries())
        .filter(([, api]) => api.isDirty())
        .sort(([a], [b]) => a - b);

    formApiRef.current = {
      validate: async () => {
        for (const [connectorId, api] of dirtyPanels()) {
          if (!(await api.validate())) {
            if (mounted.current) setSelectedId(connectorId);
            return false;
          }
        }
        return true;
      },
      saveAll: async () => {
        const failures: DoorSaveFailure[] = [];
        for (const [connectorId, api] of dirtyPanels()) {
          const error = await api.save();
          if (error) failures.push({ connectorId, error });
        }
        return failures;
      },
    };
    return () => {
      formApiRef.current = null;
    };
  }, [formApiRef]);

  const label = (connector: StationConnector): string => {
    const typeName =
      connectorTypesList?.list?.find(
        (type) => String(type.id) === String(connector.connectorTypeId),
      )?.name ?? TF('Unspecified');
    return `${TF('Connector')} ${connector.connectorId} · ${typeName}`;
  };

  const statusOf = (connectorId: number) =>
    connectorStatus(summaries.find((s) => s.connectorId === connectorId));

  const blockEnter = (event: KeyboardEvent<HTMLDivElement>) => {
    if (
      event.key === 'Enter' &&
      (event.target as HTMLElement).tagName === 'INPUT'
    )
      event.preventDefault();
  };

  const copySourcesFor = (connectorId: number): DoorCopySource[] =>
    sorted
      .filter(
        (c) =>
          c.connectorId !== connectorId &&
          statusOf(c.connectorId) === 'CONFIGURED',
      )
      .map((c) => ({ connectorId: c.connectorId, label: label(c) }));

  return (
    <DoorLayout onKeyDown={blockEnter}>
      <DoorConnectorList
        items={sorted.map((c) => {
          const summary = summaries.find(
            (s) => s.connectorId === c.connectorId,
          );
          return {
            connectorId: c.connectorId,
            label: label(c),
            enabled: !!summary?.enabled,
            configured: !!summary?.configured,
          };
        })}
        selectedId={selectedId}
        onSelect={setSelectedId}
        error={summaryError}
        onRetry={loadSummary}
        renderPanel={(connectorId) => (
          <DoorConnectorPanel
            chargeBoxPk={chargeBoxPk}
            connectorId={connectorId}
            copySources={copySourcesFor(connectorId)}
            register={register}
            onSaved={handleSaved}
          />
        )}
      />
    </DoorLayout>
  );
};

export default StationDoorAccessForm;
